import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
    Menu,
    X,
    User,
    LogOut,
    LayoutDashboard,
    ChevronDown,
    ShieldCheck,
    Phone,
    FileText,
    Users,
    CheckCircle,
    AlertCircle
} from 'lucide-react';

const Navbar = () => {
    const { adminUser, memberUser, logout } = useApp(); // Removed cart
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);

    // Dynamic User Selection for Dashboard Views
    let displayUser = null;
    if (location.pathname.startsWith('/admin')) {
        displayUser = adminUser;
    } else if (location.pathname.startsWith('/member')) {
        displayUser = memberUser;
    } else {
        // Public Pages: ALWAYS show "Sign In / Join Now" buttons.
        // This keeps the visitor view clean and guest-focused.
        displayUser = null;
    }

    // Handle logout based on active user role
    const handleLogout = () => {
        if (displayUser) {
            logout(displayUser.role); // Logout specific role
        } else {
            logout(); // Fallback
        }
        navigate('/login');
        setIsMenuOpen(false);
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isActive = (path) => location.pathname === path;

    const homePath = location.pathname.startsWith('/admin') ? '/admin' :
        location.pathname.startsWith('/member') ? '/member' : '/';
    const isOnDashboard = location.pathname.startsWith('/admin') || location.pathname.startsWith('/member');

    // SELF-HEALING: Ensure sessionStorage matches the dashboard we are actually on.
    // This fixes issues where a user duplicates an Admin tab but then logs in as Member in the new tab,
    // or navigates via history. If they are ON the dashboard, they own the tab.
    useEffect(() => {
        if (location.pathname.startsWith('/admin')) {
            sessionStorage.setItem('pci_active_role', 'admin');
        } else if (location.pathname.startsWith('/member')) {
            sessionStorage.setItem('pci_active_role', 'member');
        }
    }, [location.pathname]);

    // Simplified Navbar Rule:
    // When on Dashboard, hide Home/Products/etc links (show only profile).
    // When on Public pages, show all links + profile (if logged in).
    const showPublicLinks = !isOnDashboard;

    const navLinks = [
        { name: 'Home', path: homePath }, // Dynamically go to admin/member/public home
        { name: 'Products', path: '/products' },
        { name: 'Business Plan', path: '/income-plan' },
        { name: 'Legal', path: '/legal' },
        { name: 'Contact', path: '/contact' },
    ];

    // --- CHANGE PASSWORD MODAL LOGIC ---
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [passMsg, setPassMsg] = useState({ type: '', text: '' });
    const { updateUser, users } = useApp(); // Need users to verify current password if needed (client-side check strictly for demo)

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPassMsg({ type: '', text: '' });

        if (passwordData.new !== passwordData.confirm) {
            setPassMsg({ type: 'error', text: 'New passwords do not match.' });
            return;
        }

        if (passwordData.new.length < 6) {
            setPassMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
            return;
        }

        // Optional: Verify current password if data is available locally
        // In a real app, the backend would handle this verification.
        if (displayUser && displayUser.password && passwordData.current !== displayUser.password) {
            setPassMsg({ type: 'error', text: 'Current password is incorrect.' });
            return;
        }

        try {
            const res = await updateUser(displayUser.id, { password: passwordData.new });
            if (res.success) {
                setPassMsg({ type: 'success', text: 'Password updated successfully!' });
                setTimeout(() => {
                    setIsChangePasswordOpen(false);
                    setPasswordData({ current: '', new: '', confirm: '' });
                    setPassMsg({ type: '', text: '' });
                }, 2000);
            } else {
                setPassMsg({ type: 'error', text: res.message || 'Failed to update password.' });
            }
        } catch (err) {
            console.error(err);
            setPassMsg({ type: 'error', text: 'An unexpected error occurred.' });
        }
    };

    return (
        <nav
            className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${isScrolled
                ? 'bg-gradient-to-r from-indigo-800 via-purple-700 to-indigo-800 shadow-lg py-2'
                : 'bg-gradient-to-r from-indigo-700 via-purple-600 to-indigo-700 py-4'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center">
                    {/* Logo Section */}
                    <Link to={homePath} className="flex items-center space-x-2 group">
                        <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded-xl shadow-inner">
                                <img src="/logo.png" alt="Power Clean India" className="h-8 w-auto object-contain" />
                            </div>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {showPublicLinks && navLinks.map((link) => (
                            <Link
                                key={link.name}
                                to={link.path}
                                className={`text-sm font-medium transition-colors duration-200 ${isActive(link.path)
                                    ? 'text-white border-b-2 border-white pb-1'
                                    : 'text-indigo-100 hover:text-white'
                                    }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Auth & Cart Section */}
                    <div className="hidden md:flex items-center space-x-4">
                        {displayUser ? (
                            <div className="relative">
                                <button
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    className="flex items-center space-x-3 py-1.5 px-3 rounded-full hover:bg-white/10 transition-colors border border-transparent hover:border-white/20"
                                >
                                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-indigo-700 shadow-sm overflow-hidden border-2 border-white/20">
                                        {displayUser.profileImage ? (
                                            <img src={displayUser.profileImage} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <User className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span className="text-sm font-bold text-white leading-none mb-0.5">
                                            {displayUser.name}
                                        </span>
                                    </div>
                                    <ChevronDown className={`w-4 h-4 text-indigo-200 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showProfileMenu && (
                                    <div
                                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden transform transition-all duration-200 origin-top-right scale-100 opacity-100"
                                        onMouseLeave={() => setShowProfileMenu(false)}
                                    >
                                        <div className="p-4 border-b border-gray-50 bg-gray-50/50">
                                            <p className="text-sm font-medium text-gray-900">Signed in as</p>
                                            <p className="text-xs text-gray-500 truncate">{displayUser.email}</p>
                                        </div>

                                        <div className="p-2 space-y-1">
                                            {/* CHANGE PASSWORD OPTION (Replaces Dashboard Link) */}
                                            <button
                                                onClick={() => {
                                                    setShowProfileMenu(false);
                                                    setIsChangePasswordOpen(true);
                                                }}
                                                className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                                                <span>Change Password</span>
                                            </button>

                                            <div className="h-px bg-gray-100 my-1"></div>

                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                <span>Sign Out</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center space-x-3">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium text-white hover:text-indigo-100 transition-colors"
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    className="px-5 py-2 rounded-full bg-white text-indigo-700 text-sm font-bold hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                >
                                    Join Now
                                </Link>
                            </div>
                        )}
                    </div >

                    {/* Mobile Menu Button */}
                    < div className="md:hidden flex items-center" >
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors focus:outline-none"
                        >
                            {isMenuOpen ? (
                                <X className="w-6 h-6" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div >
                </div >
            </div >

            {/* Mobile Menu - Scroll Fix Applied */}
            <div
                className={`md:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl transition-all duration-300 ease-in-out transform max-h-[85vh] overflow-y-auto ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
                    }`}
            >
                <div className="px-4 py-6 space-y-4">
                    {showPublicLinks && navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block text-base font-medium transition-colors ${isActive(link.path)
                                ? 'text-indigo-600 bg-indigo-50 px-4 py-2 rounded-lg'
                                : 'text-gray-600 hover:text-indigo-600 px-4 py-2'
                                }`}
                        >
                            {link.name}
                        </Link>
                    ))}

                    <div className="border-t border-gray-100 pt-4 mt-4 space-y-4">
                        {displayUser ? (
                            <>
                                <div className="flex items-center px-4 space-x-3">
                                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 overflow-hidden border-2 border-indigo-50">
                                        {displayUser.profileImage ? (
                                            <img src={displayUser.profileImage} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <User className="w-6 h-6" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-base font-bold text-gray-900">{displayUser.name}</p>
                                        <div className="flex items-center mt-0.5">
                                            <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                                                ID: {displayUser.id}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile Role Action Links */}
                                <div className="px-2 space-y-2">
                                    {/* Replaced Dashboard Link with Change Password in Mobile too if requested? 
                                        Actually user said "navbar me login menu me". 
                                        In mobile menu, maybe keep Dashboard because sidebar is hidden? 
                                        User said "sidebar me h dashboard option".
                                        On mobile, sidebar IS the menu usually or separate.
                                        I will Keep Dashboard on Mobile because Sidebar might be hidden or this IS the navigation.
                                        But wait, user said "admin or member me navbar me login menu me dash bord nhi rakhna".
                                        Proceed to remove it from Mobile dropdown too if it acts as "User Menu".
                                        The mobile menu here is the MAIN navigation. 
                                        I'll replace it with "Change Password" here too to be consistent with "Profile Menu".
                                    */}
                                    <button
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            setIsChangePasswordOpen(true);
                                        }}
                                        className={`w-full flex items-center space-x-2 px-4 py-3 rounded-lg font-medium bg-gray-50 text-gray-700 active:bg-gray-100`}
                                    >
                                        <ShieldCheck className="w-5 h-5 text-indigo-600" />
                                        <span>Change Password</span>
                                    </button>

                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg border border-red-100 mt-2"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span className="font-medium">Sign Out</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="grid grid-cols-2 gap-4 px-4">
                                <Link
                                    to="/login"
                                    onClick={() => setIsMenuOpen(false)}
                                    className="flex justify-center py-2.5 border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    onClick={() => setIsMenuOpen(false)}
                                    className="flex justify-center py-2.5 bg-indigo-600 rounded-lg text-sm font-medium text-white hover:bg-indigo-700 transition-colors shadow-sm"
                                >
                                    Join Now
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div >

            {/* CHANGE PASSWORD MODAL */}
            {isChangePasswordOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-5 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-800">Change Password</h3>
                            <button onClick={() => setIsChangePasswordOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                            {passMsg.text && (
                                <div className={`p-3 rounded-xl text-sm font-bold flex items-center gap-2 ${passMsg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                    {passMsg.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                    {passMsg.text}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Current Password</label>
                                <input
                                    type="password"
                                    required
                                    value={passwordData.current}
                                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
                                    placeholder="Enter current password"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">New Password</label>
                                <input
                                    type="password"
                                    required
                                    value={passwordData.new}
                                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
                                    placeholder="Enter new password"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Confirm New Password</label>
                                <input
                                    type="password"
                                    required
                                    value={passwordData.confirm}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
                                    placeholder="Confirm new password"
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-600/20 active:scale-95"
                            >
                                Update Password
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </nav >
    );
};

export default Navbar;
